import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { FaHome, FaCog, FaUsers, FaQuestionCircle, FaHeart, FaChevronDown } from "react-icons/fa";
import {
  HiSparkles,
  HiLightningBolt,
  HiChartBar,
  HiBookOpen,
} from "react-icons/hi";
import christReignsLogo from "../../assets/crm-newlogo.png";
import LanguageSwitcher from "../common/LanguageSwitcher";

const DesktopNavigation: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState("hero");
  const [isScrolled, setIsScrolled] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  const scrollToSection = (sectionId: string, pagePath: string = "/software") => {
    if (location.pathname !== pagePath) {
      navigate(pagePath, { state: { scrollTo: sectionId, targetPage: pagePath } });
      return;
    }

    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  // Handle scroll after navigation from another page
  useEffect(() => {
    if (location.state && (location.state as any).scrollTo) {
      const sectionId = (location.state as any).scrollTo;
      const targetPage = (location.state as any).targetPage || "/software";
      
      if (location.pathname === targetPage) {
        setTimeout(() => {
          scrollToSection(sectionId, targetPage);
          // Clear state to prevent scrolling again on refresh
          window.history.replaceState({}, document.title);
        }, 100);
      }
    }
  }, [location]);

  // Track active section and scroll state
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 50);

      const sections = [
        "hero",
        "how-it-works",
        "features",
        "dashboard-preview",
        "faq",
        "cta",
        "our-story",
      ];
      const scrollPositionWithOffset = scrollPosition + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (
            scrollPositionWithOffset >= offsetTop &&
            scrollPositionWithOffset < offsetTop + offsetHeight
          ) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navigationItems = [
    { id: "/", label: t('nav.home'), type: 'route' },
    { 
      id: "/software", 
      label: t('nav.software', { defaultValue: "Christ Reigns Lab Software" }), 
      type: 'route',
      subItems: [
        { id: "how-it-works", label: t('nav.howItWorks'), type: 'scroll', page: '/software' },
        { id: "features", label: t('nav.features'), type: 'scroll', page: '/software' },
        { id: "dashboard-preview", label: t('nav.dashboard'), type: 'scroll', page: '/software' },
        { id: "faq", label: t('nav.faq'), type: 'scroll', page: '/software' },
        { id: "our-story", label: t('nav.ourStory'), type: 'scroll', page: '/software' },
        { id: "/technology", label: t('nav.technology'), type: 'route' }
      ]
    },
    { id: "/articles", label: t('nav.articles'), type: 'route' },
    { id: "/bible-and-study-resources", label: t('nav.resources', { defaultValue: "Bible & Study Resources" }), type: 'route' },
    { id: "featured-music", label: t('nav.music'), type: 'scroll', page: '/' },
    { id: "/about-us", label: t('aboutUs.title'), type: 'route' }
  ];

  const isHomePage = location.pathname === '/';

  return (
    <header
      className={`hidden xl:block fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isHomePage 
          ? (isScrolled ? "bg-black/80 backdrop-blur-md shadow-lg border-t-4" : "bg-transparent/20 backdrop-blur-sm border-t-4 border-transparent")
          : "bg-black/80 backdrop-blur-md shadow-lg border-t-4"
      }`}
      style={{ borderTopColor: (!isHomePage || isScrolled) ? '#05a5b7' : 'transparent' }}
    >
      <nav className="container lg:mx-auto px-6 xl:px-10 2xl:px-20 py-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center hover:opacity-80 transition-opacity duration-200 w-48"
          >
            <img src={christReignsLogo} alt="Christ Reigns" className="w-full" />
          </Link>

          {/* Navigation Items */}
          <div className="flex items-center space-x-1">
            {navigationItems.map((item) => (
              <div 
                key={item.id} 
                className="relative group"
                onMouseEnter={() => setHoveredItem(item.id)}
                onMouseLeave={() => setHoveredItem(null)}
              >
                {item.type === 'route' ? (
                  <Link
                    to={item.id}
                    className={`px-4 py-2 flex items-center transition-all duration-200 font-oswald text-sm uppercase tracking-wide ${
                      location.pathname === item.id || (item.subItems && item.subItems.some(sub => sub.id === activeSection))
                        ? "text-white border-b-2 border-white"
                        : "text-gray-300 hover:text-white"
                    }`}
                  >
                    {item.label}
                    {item.subItems && (
                      <FaChevronDown className={`ml-2 text-[10px] transition-transform duration-200 ${hoveredItem === item.id ? 'rotate-180' : ''}`} />
                    )}
                  </Link>
                ) : item.type === 'external' ? (
                  <a
                    href={item.id}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 flex items-center transition-all duration-200 font-oswald text-sm uppercase tracking-wide text-gray-300 hover:text-white"
                  >
                    {item.label}
                  </a>
                ) : (
                  <button
                    onClick={() => !item.subItems && scrollToSection(item.id, item.page)}
                    className={`px-4 py-2 flex items-center transition-all duration-200 font-oswald text-sm uppercase tracking-wide ${
                      activeSection === item.id || (item.subItems && item.subItems.some(sub => sub.id === activeSection))
                        ? "text-white border-b-2 border-white"
                        : "text-gray-300 hover:text-white"
                    }`}
                  >
                    {item.label}
                    {item.subItems && (
                      <FaChevronDown className={`ml-2 text-[10px] transition-transform duration-200 ${hoveredItem === item.id ? 'rotate-180' : ''}`} />
                    )}
                  </button>
                )}

                {/* Submenu Dropdown */}
                {item.subItems && (
                  <div 
                    className={`absolute top-full left-0 w-48 bg-black/95 backdrop-blur-md border border-gray-800 rounded-b-xl shadow-2xl py-2 transition-all duration-300 transform ${
                      hoveredItem === item.id ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
                    }`}
                  >
                    {item.subItems.map((sub) => (
                      sub.type === 'route' ? (
                        <Link
                          key={sub.id}
                          to={sub.id}
                          className="block px-6 py-3 text-gray-300 hover:text-white hover:bg-white/10 transition-colors font-oswald text-xs uppercase tracking-wider"
                        >
                          {sub.label}
                        </Link>
                      ) : (
                        <button
                          key={sub.id}
                          onClick={() => {
                            scrollToSection(sub.id, sub.page);
                            setHoveredItem(null);
                          }}
                          className="w-full text-left block px-6 py-3 text-gray-300 hover:text-white hover:bg-white/10 transition-colors font-oswald text-xs uppercase tracking-wider"
                        >
                          {sub.label}
                        </button>
                      )
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center space-x-4">
            <LanguageSwitcher />
            <a
              href="https://christreignsmedia.io/register"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-300 hover:text-white px-3 py-2 font-oswald text-sm uppercase tracking-wide transition-colors"
            >
              REGISTER
            </a>
            <a
              href="https://christreignsmedia.io/login"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-300 hover:text-white px-3 py-2 font-oswald text-sm uppercase tracking-wide transition-colors"
            >
              LOGIN
            </a>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default DesktopNavigation;
